"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { RiCloseLine, RiEmotionLine } from "react-icons/ri";
import Button from "@/components/ui/button";
import { DurationInput } from "@/components/ui/duration-input";
import { EmojiPicker } from "@/components/ui/emoji-picker";
import Input from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useUser } from "@/contexts/user-provider";
import { formatTime } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { useOnlineProviders, useUpdatePresence } from "@/query/team-chat";
interface StatusPreset {
  emoji: string;
  text: string;
  /** Duration in minutes, undefined = don't clear */
  durationMinutes: number | undefined;
}
const STATUS_PRESETS: StatusPreset[] = [{
  emoji: "🍽️",
  text: "On lunch",
  durationMinutes: 60
}, {
  emoji: "🤒",
  text: "Out sick",
  durationMinutes: 480
}, {
  emoji: "🏠",
  text: "Working remotely",
  durationMinutes: 480
}, {
  emoji: "🏖️",
  text: "Vacationing",
  durationMinutes: undefined
}, {
  emoji: "📞",
  text: "On a call",
  durationMinutes: 30
}, {
  emoji: "🚗",
  text: "Commuting",
  durationMinutes: 30
}];
function formatDuration(minutes: number | undefined): string {
  if (minutes === undefined) return "Don't clear";
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) {
    const h = Math.round(minutes / 60);
    return `${h}h`;
  }
  const d = Math.round(minutes / 1440);
  return `${d}d`;
}
function getExpiresAt(durationMinutes: number | undefined): string | null {
  if (durationMinutes === undefined) return null;
  return new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();
}
const handlePresenceError = (error: unknown, toast: ReturnType<typeof useToast>["toast"]) => {
  const message = error instanceof Error ? error.message : "Failed to update status";
  const status = error && typeof error === "object" && "response" in error && typeof (error as {
    response?: {
      status?: number;
    };
  }).response?.status === "number" ? (error as {
    response: {
      status: number;
    };
  }).response.status : null;
  toast({
    variant: "error",
    title: "Could not set status",
    description: status === 404 ? "Your account may not have provider access. Contact your administrator." : message
  });
};
export function SetStatusPopover() {
  const $ = _c(81);
  const [open, setOpen] = useState(false);
  const [emoji, setEmoji] = useState(null);
  const [text, setText] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(240);
  const {
    toast
  } = useToast();
  const {
    currentUser
  } = useUser();
  const {
    data: onlineData
  } = useOnlineProviders();
  const updatePresence = useUpdatePresence();
  let t0;
  if ($[0] !== toast) {
    t0 = error => handlePresenceError(error, toast);
    $[0] = toast;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let T0;
  let T1;
  let handleClear;
  let handleSave;
  let hasStatus;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== currentUser?.id || $[3] !== durationMinutes || $[4] !== emoji || $[5] !== onlineData?.providers || $[6] !== open || $[7] !== t0 || $[8] !== text || $[9] !== updatePresence) {
    const mutationOptions = {
      onError: t0
    };
    const currentPresence = onlineData?.providers?.find(p => p.providerId === currentUser?.id);
    hasStatus = !!currentPresence?.customStatusText;
    const isWithClient = currentPresence?.status === "WITH_CLIENT";
    const appointment = currentPresence?.currentAppointment;
    const appointmentEndTime = appointment ? formatTime(appointment.endTime) : "";
    const isBusy = currentPresence?.status === "BUSY";
    const block = currentPresence?.currentBlock;
    const blockEndTime = block ? formatTime(block.endTime) : "";
    const handleOpen = isOpen => {
      if (isOpen) {
        setEmoji(currentPresence?.customStatusEmoji ?? null);
        setText(currentPresence?.customStatusText ?? "");
        setDurationMinutes(240);
      }
      setOpen(isOpen);
    };
    handleSave = () => {
      updatePresence.mutate({
        customStatusText: text.trim() || null,
        customStatusEmoji: emoji,
        customStatusExpiresAt: getExpiresAt(durationMinutes)
      }, mutationOptions);
      setOpen(false);
    };
    handleClear = () => {
      updatePresence.mutate({
        customStatusText: null,
        customStatusEmoji: null,
        customStatusExpiresAt: null
      }, mutationOptions);
      setEmoji(null);
      setText("");
      setOpen(false);
    };
    const handlePreset = preset => {
      updatePresence.mutate({
        customStatusText: preset.text,
        customStatusEmoji: preset.emoji,
        customStatusExpiresAt: getExpiresAt(preset.durationMinutes)
      }, mutationOptions);
      setOpen(false);
    };
    T1 = Popover;
    t9 = open;
    t10 = handleOpen;
    t11 = false;
    const t13 = hasStatus ? "bg-hover text-foreground-secondary hover:text-foreground" : "text-foreground-muted hover:text-foreground";
    let t14;
    if ($[27] !== t13) {
      t14 = cx("shrink-0 gap-1", t13);
      $[27] = t13;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    const t15 = <Button variant="ghost" size="sm" className={t14}>{hasStatus && currentPresence?.customStatusEmoji ? <span className="text-body leading-none">{currentPresence.customStatusEmoji}</span> : <RiEmotionLine className="h-3.5 w-3.5" />}<span>{hasStatus ? currentPresence?.customStatusText : "Set status"}</span></Button>;
    if ($[29] !== t15) {
      t12 = <PopoverTrigger asChild={true}>{t15}</PopoverTrigger>;
      $[29] = t15;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    T0 = PopoverContent;
    t3 = "start";
    t4 = "w-80 p-0";
    t5 = _temp;
    let t16;
    let t17;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <p className="text-body-emphasis text-foreground">Set a status</p>;
      t17 = () => setOpen(false);
      $[31] = t16;
      $[32] = t17;
    } else {
      t16 = $[31];
      t17 = $[32];
    }
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex items-center justify-between border-b border-border px-4 py-2.5">{t16}<button onClick={t17} className="rounded p-0.5 text-foreground-muted transition-colors hover:text-foreground"><RiCloseLine className="h-4 w-4" /></button></div>;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    const t18 = emoji ?? "\uD83D\uDE00";
    let t19;
    if ($[34] !== t18) {
      t19 = <PopoverTrigger asChild={true}><button type="button" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border text-body-lg transition-colors hover:bg-hover">{t18}</button></PopoverTrigger>;
      $[34] = t18;
      $[35] = t19;
    } else {
      t19 = $[35];
    }
    const t20 = emoji ?? undefined;
    let t21;
    if ($[36] !== t20) {
      t21 = <PopoverContent side="bottom" align="start" className="max-h-64 w-64 overflow-y-auto p-3" onPointerDownOutside={_temp2} onInteractOutside={_temp3}><EmojiPicker selectedEmoji={t20} onSelect={setEmoji} closeOnSelect={true} /></PopoverContent>;
      $[36] = t20;
      $[37] = t21;
    } else {
      t21 = $[37];
    }
    let t22;
    if ($[38] !== t19 || $[39] !== t21) {
      t22 = <Popover>{t19}{t21}</Popover>;
      $[38] = t19;
      $[39] = t21;
      $[40] = t22;
    } else {
      t22 = $[40];
    }
    let t23;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = e_2 => setText(e_2.target.value);
      $[41] = t23;
    } else {
      t23 = $[41];
    }
    const t24 = <div className="flex items-center gap-2">{t22}<Input placeholder="What's your status?" value={text} onChange={t23} onKeyDown={e_3 => {
        if (e_3.key === "Enter") {
          e_3.preventDefault();
          handleSave();
        }
      }} className="flex-1" autoFocus={true} /></div>;
    let t25;
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <span className="shrink-0 text-caption text-foreground-muted">Clear after</span>;
      $[42] = t25;
    } else {
      t25 = $[42];
    }
    let t26;
    if ($[43] !== durationMinutes) {
      t26 = <div className="flex items-center gap-2">{t25}<DurationInput value={durationMinutes} onChange={setDurationMinutes} defaultUnit="hours" placeholder="0" className="flex-1" /></div>;
      $[43] = durationMinutes;
      $[44] = t26;
    } else {
      t26 = $[44];
    }
    if ($[45] !== t24 || $[46] !== t26) {
      t7 = <div className="space-y-2 border-b border-border px-4 py-3">{t24}{t26}</div>;
      $[45] = t24;
      $[46] = t26;
      $[47] = t7;
    } else {
      t7 = $[47];
    }
    t8 = isWithClient && appointment || isBusy && block ? <div className="border-b border-border px-4 py-2"><p className="mb-1 text-tiny font-semibold uppercase tracking-wider text-foreground-muted">Automatically set</p>{isWithClient && appointment && <div className="flex items-center gap-3 rounded-md bg-status-warning-bg px-3 py-2"><span className="text-body-lg">🩺</span><div className="min-w-0 flex-1"><p className="text-body-strong text-foreground">With a patient</p>{appointmentEndTime ? <p className="text-caption text-foreground-muted">Until {appointmentEndTime}</p> : null}</div></div>}{isBusy && block && <div className={cx("flex items-center gap-3 rounded-md bg-status-error-bg px-3 py-2", isWithClient && appointment && "mt-2")}><span className="text-body-lg">🚫</span><div className="min-w-0 flex-1"><p className="text-body-strong text-foreground">Busy</p>{blockEndTime ? <p className="text-caption text-foreground-muted">Until {blockEndTime}</p> : null}</div></div>}</div> : null;
    t1 = "max-h-[200px] overflow-y-auto border-b border-border py-1";
    t2 = STATUS_PRESETS.map(preset_0 => <button key={preset_0.text} onClick={() => handlePreset(preset_0)} className="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-hover"><span className="text-body-lg">{preset_0.emoji}</span><div className="min-w-0 flex-1"><span className="text-body text-foreground">{preset_0.text}</span></div><span className="shrink-0 text-caption text-foreground-muted">{formatDuration(preset_0.durationMinutes)}</span></button>);
    $[2] = currentUser?.id;
    $[3] = durationMinutes;
    $[4] = emoji;
    $[5] = onlineData?.providers;
    $[6] = open;
    $[7] = t0;
    $[8] = text;
    $[9] = updatePresence;
    $[10] = T0;
    $[11] = T1;
    $[12] = handleClear;
    $[13] = handleSave;
    $[14] = hasStatus;
    $[15] = t1;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    T0 = $[10];
    T1 = $[11];
    handleClear = $[12];
    handleSave = $[13];
    hasStatus = $[14];
    t1 = $[15];
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t13;
  if ($[48] !== t1 || $[49] !== t2) {
    t13 = <div className={t1}>{t2}</div>;
    $[48] = t1;
    $[49] = t2;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  const t14 = !hasStatus && !text && !emoji;
  let t15;
  if ($[51] !== handleClear || $[52] !== t14) {
    t15 = <button onClick={handleClear} disabled={t14} className="text-caption text-foreground-secondary transition-colors hover:text-foreground disabled:opacity-40">Clear status</button>;
    $[51] = handleClear;
    $[52] = t14;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== emoji || $[55] !== text || $[56] !== updatePresence.isPending) {
    t16 = updatePresence.isPending || !text.trim() && !emoji;
    $[54] = emoji;
    $[55] = text;
    $[56] = updatePresence.isPending;
    $[57] = t16;
  } else {
    t16 = $[57];
  }
  let t17;
  if ($[58] !== handleSave || $[59] !== t16) {
    t17 = <Button onClick={handleSave} variant="primary" className="h-7 px-4 text-caption" disabled={t16}>Save</Button>;
    $[58] = handleSave;
    $[59] = t16;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== t15 || $[62] !== t17) {
    t18 = <div className="flex items-center justify-between px-4 py-2.5">{t15}{t17}</div>;
    $[61] = t15;
    $[62] = t17;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] !== T0 || $[65] !== t13 || $[66] !== t18 || $[67] !== t3 || $[68] !== t4 || $[69] !== t5 || $[70] !== t6 || $[71] !== t7 || $[72] !== t8) {
    t19 = <T0 align={t3} className={t4} onOpenAutoFocus={t5}>{t6}{t7}{t8}{t13}{t18}</T0>;
    $[64] = T0;
    $[65] = t13;
    $[66] = t18;
    $[67] = t3;
    $[68] = t4;
    $[69] = t5;
    $[70] = t6;
    $[71] = t7;
    $[72] = t8;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  let t20;
  if ($[74] !== T1 || $[75] !== t10 || $[76] !== t11 || $[77] !== t12 || $[78] !== t19 || $[79] !== t9) {
    t20 = <T1 open={t9} onOpenChange={t10} modal={t11}>{t12}{t19}</T1>;
    $[74] = T1;
    $[75] = t10;
    $[76] = t11;
    $[77] = t12;
    $[78] = t19;
    $[79] = t9;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  return t20;
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(e) {
  return e.preventDefault();
}